import classes from '@/App.module.scss';

import { API_PREDICT_ENDPOINT, createApiGetImportFileEndpoint } from '@/api/config';

import {
  Alert,
  FileInput,
  Button,
  Group,
  Text,
  Stack,
  Box,
  Checkbox,
} from '@mantine/core';
import { Link, useLocation } from 'react-router-dom';
import { useState, useCallback, useEffect } from 'react';
import { HttpStatusCode, isAxiosError } from 'axios';
import apiClient from '@/api/client';
import { useLanguage } from '@/i18n/LanguageContext';

type SuccessResponse = {
  message: string;
  error_message?: string | null;
  error_code?: string | null;
  count: number;
  duplicates_count: number;
  measurement_time_adjusted_count: number;
  invalid_row_errors?: string[];
  isValidFile: boolean;
};

type ApiErrorResponse = {
  error_message?: string | null;
  error_code?: string | null;
  detail?: string;
  message?: string;
};

type DisplayedError = {
  message: string;
};

export default function UploadButton() {
  const location = useLocation();
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [response, setResponse] = useState<SuccessResponse | null>(null);
  const [error, setError] = useState<DisplayedError | null>(null);
  const [allowDuplicates, setAllowDuplicates] = useState<boolean>(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const invalidRowErrors = response?.invalid_row_errors ?? [];

  // Auto-load file from navigation state
  useEffect(() => {
    const state = location.state as { importFileId?: number; fileName?: string } | null;
    if (state?.importFileId && state?.fileName) {
      const loadFileFromImport = async () => {
        setLoadingFile(true);
        try {
          const response = await apiClient({
            url: createApiGetImportFileEndpoint(state.importFileId!),
            method: 'GET',
            responseType: 'blob',
          });

          const blob = response.data;
          const file = new File([blob], state.fileName!, { type: 'text/csv' });
          setFile(file);
        } catch (error) {
          console.error('Error loading file from import:', error);
          setError({
            message: t('upload.importLoadError'),
          });
        } finally {
          setLoadingFile(false);
        }
      };

      loadFileFromImport();
    }
  }, [location.state, t]);

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    setResponse(null);
    setError(null);
  };

  const handleSubmit = useCallback(async () => {
    if (!file) return;

    const data = new FormData();
    data.set('file', file);
    data.set('allow_duplicates', JSON.stringify(allowDuplicates));

    try {
      const response = await apiClient({
        url: API_PREDICT_ENDPOINT,
        method: 'POST',
        data: data,
        responseType: 'json',
      });

      setFile(null);
      setError(null);

      if (response.status !== HttpStatusCode.Ok) {
        setError({
          message: t('upload.failed'),
        });
        return;
      }
      const responseData = response.data as SuccessResponse | null;
      if (!responseData) {
        setError({
          message: t('upload.processingFailed'),
        });
        return;
      }

      if (!responseData.message || responseData.count <= 0) {
        const detailedMessage = responseData.error_message?.trim();
        if (responseData.duplicates_count > 0) {
          setError({
            message: t('upload.duplicatesDetected', {
              count: responseData.duplicates_count,
            }),
          });
          return;
        }

        if (detailedMessage || responseData.message) {
          setError({
            message: detailedMessage || responseData.message,
          });
          return;
        }

        if (!responseData.isValidFile) {
          setError({
            message: t('upload.invalidFormat'),
          });
          return;
        }

        setError({
          message: t('upload.processingFailed'),
        });
        return;
      }
      setResponse(responseData);
    } catch (error) {
      setResponse(null);
      const apiError = isAxiosError<ApiErrorResponse>(error)
        ? error.response?.data
        : null;
      const apiMessage =
        apiError?.error_message?.trim() ||
        apiError?.message ||
        apiError?.detail;
      setError({
        message:
          apiMessage ||
          t('upload.serverError', {
            message: error instanceof Error ? error.message : t('upload.unknownError'),
          }),
      });
    }
  }, [file, allowDuplicates, t]);

  return (
    <Box className={classes.heroSection}>
      <Stack className={classes.uploadPanel} gap="md">
        <div>
          <Text className={classes.uploadHint}>
            {t('upload.description')}
          </Text>
        </div>

        <div className={classes.fileInput}>
          <FileInput
            label={t('upload.selectFile')}
            placeholder={loadingFile ? t('upload.loadingFile') : t('upload.chooseFile')}
            value={file}
            onChange={handleFileChange}
            accept=".csv,text/csv"
            clearable
            disabled={loadingFile}
            error={
              file && file.size > 5 * 1024 * 1024
                ? t('upload.fileTooLarge')
                : null
            }
          />
        </div>

        {file && (
          <Group align="center" gap="sm">
            <Text size="sm" c="dimmed">
              {t('upload.selected', {
                name: file.name,
                size: Math.round(file.size / 1024),
              })}
            </Text>
            <Checkbox
              label={t('upload.allowDuplicates')}
              checked={allowDuplicates}
              onChange={(event) =>
                setAllowDuplicates(event.currentTarget.checked)
              }
            />
            <Button color="green" onClick={handleSubmit} disabled={!file}>
              {t('upload.submit')}
            </Button>
          </Group>
        )}

        {response && (
          <>
            <Text size="md">
              {response.message} <br />
            </Text>
            <Text size="md">
              {t('upload.recordsProcessed', {
                count: response.count,
                duplicates: response.duplicates_count,
              })}
            </Text>
            {response.measurement_time_adjusted_count > 0 && (
              <Alert color="yellow">
                {t('upload.measurementTimeAdjusted', {
                  count: response.measurement_time_adjusted_count,
                })}
              </Alert>
            )}
            {invalidRowErrors.length > 0 && (
              <Alert
                color="orange"
                title={t('upload.rowsSkipped', {
                  count: invalidRowErrors.length,
                })}
              >
                <Stack gap={4}>
                  {invalidRowErrors.slice(0, 5).map((message, index) => (
                    <Text size="sm" key={`${index}-${message}`}>
                      {message}
                    </Text>
                  ))}
                  {invalidRowErrors.length > 5 && (
                    <Text size="sm">
                      {t('upload.moreErrors', {
                        count: invalidRowErrors.length - 5,
                      })}
                    </Text>
                  )}
                </Stack>
              </Alert>
            )}
            <Text size="md">
              {t('upload.viewResult')}{' '}
              <Link
                style={{ color: '#046536', fontWeight: 700, textDecoration: 'none' }}
                to="/inference_results"
              >
                {t('upload.here')}
              </Link>
            </Text>
          </>
        )}

        {error && (
          <Alert color="red">
            <Text component="div" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {error.message}
            </Text>
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
